import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isApiError, refusalMessage } from '../api';
import { customerApi } from '../api/customers';
import { clearCustomerSession, startCustomerSession } from '../lib/customerSession';
import './profileEditPremium.css';

function BackArrow() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <path
        d="M15.5 18.5L8.5 12l7-7.5"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function initialsFromName(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0].slice(0, 1)}${parts[parts.length - 1].slice(0, 1)}`.toUpperCase();
}

/** The name as the app asks for it, split at its first space as sign-up and the import split it. */
function splitName(fullName) {
  const [first = '', ...rest] = String(fullName).trim().split(/\s+/);

  return { first, last: rest.length ? rest.join(' ') : null };
}

/**
 * The client corrects their own details on InGo's API (`PATCH customer/me`,
 * ingo-e0, 2 Oct 2026). Only what changed is sent. A new email is how they
 * sign in, so the server asks their current password for it, and the field
 * appears only then. An address or number someone else holds is refused
 * without naming them; a changed one is no longer marked verified. The phone
 * is typed however they like ("0772 123 456"); the server keeps it as
 * +263…. No photo: InGo keeps none for a client yet.
 */
export default function ProfileEditPage({ customers = customerApi }) {
  const navigate = useNavigate();
  const [saved, setSaved] = useState(null);
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [saving, setSaving] = useState(false);

  // An answer about what was sent goes once they change it.
  const edit = (set) => (ev) => {
    setErrorMessage('');
    set(ev.target.value);
  };

  const signInAgain = () => {
    clearCustomerSession();
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    customers.me().then(
      (account) => {
        if (cancelled) return;
        setSaved(account);
        setFullName([account.first_name, account.last_name].filter(Boolean).join(' '));
        setPhone(account.phone ?? '');
        setEmail(account.email ?? '');
      },
      (err) => {
        if (cancelled) return;
        if (isApiError(err) && err.status === 401) {
          clearCustomerSession();
          navigate('/login', { replace: true });
          return;
        }
        setErrorMessage(refusalMessage(err));
      },
    );

    return () => {
      cancelled = true;
    };
  }, [customers, navigate]);

  const initials = useMemo(() => initialsFromName(fullName), [fullName]);
  const emailChanged = saved !== null && email.trim().toLowerCase() !== String(saved.email ?? '').toLowerCase();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (!saved) return;

    const { first, last } = splitName(fullName);
    if (!first) {
      setErrorMessage('Enter your name.');
      return;
    }
    if (emailChanged && currentPassword === '') {
      setErrorMessage('Enter your current password to change your email.');
      return;
    }

    const changes = {};
    if (first !== saved.first_name) changes.first_name = first;
    if (last !== (saved.last_name ?? null)) changes.last_name = last;
    if (phone.trim() !== (saved.phone ?? '')) changes.phone = phone.trim() === '' ? null : phone.trim();
    if (emailChanged) {
      changes.email = email.trim().toLowerCase();
      changes.current_password = currentPassword;
    }

    if (Object.keys(changes).length === 0) {
      navigate('/profile', { replace: true });
      return;
    }

    setSaving(true);
    try {
      const { account } = await customers.updateMe(changes);
      startCustomerSession(account);
      navigate('/profile', { replace: true });
    } catch (err) {
      if (isApiError(err) && err.status === 401) {
        signInAgain();
        return;
      }
      setErrorMessage(refusalMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="prf-edit-page" role="main" aria-label="Edit profile">
      <header className="prf-edit-nav">
        <Link to="/profile" className="prf-edit-nav__back" aria-label="Back to profile">
          <BackArrow />
        </Link>
        <h1 className="prf-edit-nav__title">Edit Profile</h1>
        <span aria-hidden />
      </header>

      <div className="prf-edit-scroll">
        <div className="prf-edit-photo">
          <div className="prf-edit-photo__wrap">
            <div className="prf-edit-photo__avatar" aria-hidden>
              {initials}
            </div>
          </div>
        </div>

        <form id="prf-edit-form" className="prf-edit-card" onSubmit={handleSubmit} noValidate>
          <h2 className="prf-edit-card__heading">Personal information</h2>

          <div className="prf-edit-field">
            <label className="prf-edit-label" htmlFor="prf-edit-name">
              Full name
            </label>
            <div className="prf-edit-input-wrap">
              <input
                id="prf-edit-name"
                className="prf-edit-input"
                value={fullName}
                onChange={edit(setFullName)}
                autoComplete="name"
                placeholder="Your full name"
                disabled={!saved}
              />
            </div>
          </div>

          <div className="prf-edit-field">
            <label className="prf-edit-label" htmlFor="prf-edit-phone">
              Phone
            </label>
            <div className="prf-edit-input-wrap">
              <input
                id="prf-edit-phone"
                className="prf-edit-input"
                value={phone}
                onChange={edit(setPhone)}
                autoComplete="tel"
                inputMode="tel"
                placeholder="0772 123 456"
                disabled={!saved}
              />
            </div>
          </div>

          <div className="prf-edit-field">
            <label className="prf-edit-label" htmlFor="prf-edit-email">
              Email
            </label>
            <div className="prf-edit-input-wrap">
              <input
                id="prf-edit-email"
                className="prf-edit-input"
                type="email"
                value={email}
                onChange={edit(setEmail)}
                autoComplete="email"
                placeholder="Email address"
                disabled={!saved}
              />
            </div>
          </div>

          {emailChanged ? (
            <div className="prf-edit-field">
              <label className="prf-edit-label" htmlFor="prf-edit-current-password">
                Current password
              </label>
              <div className="prf-edit-input-wrap">
                <input
                  id="prf-edit-current-password"
                  className="prf-edit-input"
                  type="password"
                  value={currentPassword}
                  onChange={edit(setCurrentPassword)}
                  autoComplete="current-password"
                />
              </div>
              <p className="prf-edit-label" style={{ marginTop: '0.4rem', fontWeight: 400 }}>
                Your email is how you sign in, so changing it needs your password.
              </p>
            </div>
          ) : null}

          {errorMessage ? (
            <p className="prf-edit-error" role="alert">
              {errorMessage}
            </p>
          ) : null}
        </form>
      </div>

      <div className="prf-edit-footer">
        <button type="submit" form="prf-edit-form" className="prf-edit-save" disabled={saving || !saved}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
